"use client";

import Link from "next/link";
import { useRouter } from "next/navigation";
import { useCallback, useEffect, useState } from "react";
import Icon, { type IconName } from "../../components/Icon";
import { EsqueletoPanel, Hueso } from "../../components/Esqueleto";
import Avatar from "../../components/Avatar";
import PushToggle from "../../components/PushToggle";
import SubirFoto from "../../components/SubirFoto";
import { api, clearTokens, hasSession, isAuthError } from "../../lib/api";

interface KycVerification {
  id: string;
  type: "IDENTITY" | "BACKGROUND" | "TRADE";
  status: "PENDING" | "APPROVED" | "REJECTED" | "EXPIRED";
  reviewNote?: string;
  createdAt: string;
}

interface Me {
  id: string;
  fullName: string;
  phone?: string;
  email?: string;
  kycLevel: number;
  roles: { role: string }[];
  techProfile?: { displayName: string } | null;
  business?: { name: string } | null;
  kycVerifications: KycVerification[];
}

const KYC_STEPS = [
  {
    type: "IDENTITY" as const,
    level: 1,
    title: "Identidad",
    desc: "Foto de tu CI (frente) y una selfie sosteniéndola",
    forAll: true,
  },
  {
    type: "BACKGROUND" as const,
    level: 2,
    title: "Antecedentes",
    desc: "Certificado de antecedentes policial o judicial (foto o PDF)",
    forAll: false,
  },
  {
    type: "TRADE" as const,
    level: 3,
    title: "Oficio",
    desc: "Título, certificados o fotos de trabajos que hiciste",
    forAll: false,
  },
];

const STATUS_CHIP: Record<string, { label: string; cls: string; icon?: IconName }> = {
  PENDING: { label: "En revisión", cls: "bg-[#72A9D8]/15 text-[#72A9D8]" },
  APPROVED: { label: "Aprobada", cls: "bg-[#5CBE8F]/15 text-[#5CBE8F]", icon: "check" },
  REJECTED: { label: "Rechazada", cls: "bg-[#DE705C]/15 text-[#DE705C]" },
  EXPIRED: { label: "Vencida", cls: "bg-white/10 text-[#B5C0BB]" },
};

export default function Perfil() {
  const router = useRouter();
  const [me, setMe] = useState<Me | null>(null);
  const [error, setError] = useState("");
  const [errorCarga, setErrorCarga] = useState("");
  const [uploading, setUploading] = useState<string | null>(null);
  // Cambia al subir o quitar la foto: sin esto el navegador sigue mostrando la
  // anterior de su caché y parece que no se guardó.
  const [fotoV, setFotoV] = useState<number | null>(null);

  const load = useCallback(async () => {
    setErrorCarga("");
    try {
      setMe(await api<Me>("/auth/me"));
    } catch (e) {
      // Borrar la sesión solo si el servidor dice que no vale (401). Un 500, un
      // reinicio del API o un bajón de señal no pueden echar al usuario: el que
      // entró con Google no tiene contraseña con la que volver.
      if (isAuthError(e)) {
        clearTokens();
        router.push("/ingresar");
        return;
      }
      setErrorCarga(e instanceof Error ? e.message : "No pudimos cargar tu perfil");
    }
  }, [router]);

  useEffect(() => {
    if (!hasSession()) {
      router.push("/ingresar");
      return;
    }
    load();
  }, [load, router]);

  async function upload(type: string, file: File) {
    setError("");
    setUploading(type);
    try {
      const fd = new FormData();
      fd.append("type", type);
      fd.append("archivo", file);
      await api("/kyc", { method: "POST", body: fd });
      await load();
    } catch (e) {
      setError(e instanceof Error ? e.message : "No se pudo subir el archivo");
    } finally {
      setUploading(null);
    }
  }

  if (!me) {
    if (errorCarga) {
      return (
        <main className="min-h-screen bg-[#14191B] text-[#EDF1EF] flex items-center justify-center p-6">
          <div className="max-w-sm w-full text-center flex flex-col gap-4">
            <Icon name="alert" size={34} className="mx-auto text-[#E8A23C]" />
            <div>
              <p className="text-lg font-bold">No pudimos cargar tu perfil</p>
              <p className="text-sm text-[#808D88] mt-1">{errorCarga}</p>
            </div>
            <button
              onClick={() => load()}
              className="rounded-xl bg-[#E8A23C] text-[#201404] font-bold py-3 hover:brightness-105"
            >
              Reintentar
            </button>
            <p className="text-[12px] text-[#808D88]">
              Tu sesión sigue abierta: no hace falta que vuelvas a ingresar.
            </p>
          </div>
        </main>
      );
    }
    return (
      <main className="min-h-screen bg-[#14191B] text-[#EDF1EF] p-6">
        <div className="max-w-md mx-auto flex flex-col gap-6" aria-busy="true" aria-label="Cargando tu perfil">
          <div className="flex items-center gap-4">
            <Hueso className="w-14 h-14 !rounded-full flex-none" />
            <div className="flex-1 space-y-2">
              <Hueso className="h-5 w-40" />
              <Hueso className="h-3.5 w-28" />
            </div>
          </div>
          <EsqueletoPanel alto="h-32" />
          <EsqueletoPanel alto="h-24" />
          <EsqueletoPanel alto="h-44" />
        </div>
      </main>
    );
  }

  const isPro = me.roles.some((r) => r.role === "TECH" || r.role === "BUSINESS");
  const latestByType = (type: string) =>
    me.kycVerifications.find((v) => v.type === type);

  return (
    <main className="min-h-screen bg-[#14191B] text-[#EDF1EF] p-6">
      <div className="max-w-md mx-auto flex flex-col gap-6">
        <header className="flex items-center gap-4">
          <Avatar userId={me.id} nombre={me.fullName} size={56} version={fotoV} className="!rounded-full" />
          <div className="flex-1">
            <h1 className="text-xl font-bold">{me.fullName}</h1>
            <p className="text-sm text-[#808D88]">
              {me.techProfile?.displayName ?? me.business?.name ?? me.phone ?? me.email}
            </p>
            <div className="flex gap-1.5 mt-1.5 flex-wrap">
              {me.roles.map((r) => (
                <span key={r.role} className="text-[11px] font-semibold bg-white/10 rounded-full px-2.5 py-0.5">
                  {{ CLIENT: "Cliente", TECH: "Técnico", BUSINESS: "Local", ADMIN: "Admin", SUPPORT: "Soporte" }[r.role] ?? r.role}
                </span>
              ))}
            </div>
          </div>
        </header>

        <nav className="grid grid-cols-2 gap-2">
          {me.roles.some((r) => r.role === "BUSINESS") && (
            <Link href="/local" className="inline-flex items-center justify-center gap-2 rounded-xl bg-[#E8A23C] text-[#201404] font-bold py-3 hover:brightness-105">
              <Icon name="store" size={18} strokeWidth={2.25} /> Mi local
            </Link>
          )}
          {me.roles.some((r) => r.role === "TECH") && (
            <Link href="/tecnico" className="inline-flex items-center justify-center gap-2 rounded-xl bg-[#E8A23C] text-[#201404] font-bold py-3 hover:brightness-105">
              <Icon name="wrench" size={18} strokeWidth={2.25} /> Mis trabajos
            </Link>
          )}
          {[
            { href: "/buscar", icon: "search" as const, label: "Buscar técnico" },
            { href: "/solicitudes", icon: "list" as const, label: "Mis reparaciones" },
            { href: "/tienda", icon: "cart" as const, label: "Tienda" },
            { href: "/tienda/pedidos", icon: "package" as const, label: "Mis pedidos" },
            { href: "/plan", icon: "card" as const, label: "Mi plan" },
          ].map((l) => (
            <Link key={l.href} href={l.href}
              className="inline-flex items-center justify-center gap-2 rounded-xl border border-white/15 bg-white/5 font-semibold py-3 hover:bg-white/10">
              <Icon name={l.icon} size={17} /> {l.label}
            </Link>
          ))}
        </nav>

        <SubirFoto userId={me.id} nombre={me.fullName} onCambio={() => setFotoV(Date.now())} />

        <PushToggle />

        <section>
          <div className="flex items-baseline justify-between mb-3">
            <h2 className="font-mono text-xs tracking-[0.15em] uppercase text-[#808D88]">
              Tu verificación
            </h2>
            <span className="text-sm font-semibold text-[#E8A23C]">
              Nivel {me.kycLevel} de {isPro ? 3 : 1}
            </span>
          </div>
          <div className="flex flex-col gap-3">
            {KYC_STEPS.filter((s) => s.forAll || isPro).map((step) => {
              const v = latestByType(step.type);
              const chip = v ? STATUS_CHIP[v.status] : null;
              const canUpload = !v || v.status === "REJECTED" || v.status === "EXPIRED";
              return (
                <div key={step.type} className="rounded-xl bg-[#1A2123] border border-white/10 p-4">
                  <div className="flex items-center justify-between gap-2">
                    <strong className="text-[15px]">N{step.level} · {step.title}</strong>
                    {chip && (
                      <span className={`inline-flex items-center gap-1 text-[11px] font-semibold rounded-full px-2.5 py-0.5 ${chip.cls}`}>
                        {chip.icon && <Icon name={chip.icon} size={13} strokeWidth={2.5} />}
                        {chip.label}
                      </span>
                    )}
                  </div>
                  <p className="text-[13px] text-[#808D88] mt-1">{step.desc}</p>
                  {v?.status === "REJECTED" && v.reviewNote && (
                    <p className="text-[13px] text-[#DE705C] mt-2">Motivo: {v.reviewNote}</p>
                  )}
                  {canUpload && (
                    <label className={`mt-3 inline-flex items-center gap-2 rounded-lg px-4 py-2.5 text-sm font-bold cursor-pointer transition ${
                      uploading === step.type
                        ? "bg-white/10 text-[#808D88]"
                        : "bg-[#E8A23C] text-[#201404] hover:brightness-105"
                    }`}>
                      {uploading === step.type ? "Subiendo…" : v ? "Volver a subir" : <><Icon name="upload" size={18} /> Subir documento</>}
                      <input type="file" accept="image/jpeg,image/png,image/webp,application/pdf" className="hidden"
                        disabled={uploading !== null}
                        onChange={(e) => {
                          const f = e.target.files?.[0];
                          if (f) upload(step.type, f);
                          e.target.value = "";
                        }} />
                    </label>
                  )}
                </div>
              );
            })}
          </div>
          {error && <p className="text-sm text-[#DE705C] bg-[#DE705C]/10 rounded-lg px-4 py-2.5 mt-3">{error}</p>}
          <p className="text-[12px] text-[#808D88] mt-3">
            Los documentos se guardan en un depósito privado y solo los ve el equipo de verificación.
            Los perfiles verificados aparecen más arriba y generan más confianza.
          </p>
        </section>

        <button
          onClick={() => { clearTokens(); router.push("/"); }}
          className="rounded-xl border border-white/15 py-3 text-sm font-semibold text-[#DE705C] hover:bg-white/5 transition"
        >
          Cerrar sesión
        </button>
      </div>
    </main>
  );
}
